import { useEffect, useRef, useState } from 'react';
import type { IconDefinition } from '../../shared/iconDefinition';
import type { ReplayReactionEvent } from '../../shared/replayReactionEvent';
import { TwemojiGlyphImage } from '../twemojiGlyphImage';
import { createReplayStreamScheduler } from './replayStreamScheduler';

// Spec §6.3. Chosen against a spike of forty simultaneous taps: enough to read as
// "everyone felt this at once", few enough to still see individual icons.
const MAX_SPAWNS_PER_FRAME = 6;

// Matches the CSS animation duration in appStyles.css.
const FLOAT_DURATION_MS = 1800;

// Hard ceiling on live DOM nodes, independent of the per-frame cap. A long stall
// followed by a seek can produce several capped frames back to back.
const MAX_LIVE_SPRITES = 90;

type Sprite = { key: number; iconId: string; leftPct: number; topPct: number };

// Floats every reviewer's reactions over the video at the moment they were felt.
//
// This is the most visceral read of the feedback available, and it is the feature
// creators are most likely to come back for. It complements the charts rather
// than replacing them: nobody can count icons flying past, so it conveys feel,
// not magnitude — the numbers still live in the timeline underneath.
//
// It renders only where spec §6.3 permits: the creator dashboard behind
// dashboard_key, and a reviewer's own summary screen after they have finished.
// Never during a first watch.
export function ReactionReplayOverlay(input: {
  enabled: boolean;
  events: ReplayReactionEvent[];
  icons: IconDefinition[];
  readCurrentTimeMs: () => number;
}) {
  const [sprites, setSprites] = useState<Sprite[]>([]);
  const spriteKeyRef = useRef(0);

  // Held in a ref, and deliberately NOT an effect dependency.
  //
  // Callers pass an inline arrow, so its identity changes on every render of the
  // parent. As a dependency it tore down and rebuilt the scheduler continuously,
  // which had two consequences, neither visible as an error: the cursor reset to
  // the start each time, and — worse — every pending sprite-removal timeout was
  // neutered by its own `cancelled` flag, so sprites accumulated and were never
  // cleared. Reading through a ref keeps the loop alive across renders while
  // still always calling the latest function.
  const readTimeRef = useRef(input.readCurrentTimeMs);
  readTimeRef.current = input.readCurrentTimeMs;
  const iconById = new Map(input.icons.map((icon) => [icon.id, icon]));

  useEffect(() => {
    if (!input.enabled) {
      setSprites([]);
      return;
    }

    const scheduler = createReplayStreamScheduler(input.events, {
      maxPerFrame: MAX_SPAWNS_PER_FRAME
    });
    scheduler.reset(readTimeRef.current());

    let frame = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const due = scheduler.due(readTimeRef.current());
      if (due.length > 0) {
        const spawned: Sprite[] = due.map((event) => {
          spriteKeyRef.current += 1;
          return {
            key: spriteKeyRef.current,
            iconId: event.icon_id,
            // Randomised on both axes so simultaneous reactions stay legible
            // instead of stacking into one opaque glyph.
            leftPct: 8 + Math.random() * 84,
            topPct: 25 + Math.random() * 55
          };
        });
        setSprites((current) => [...current, ...spawned].slice(-MAX_LIVE_SPRITES));
        window.setTimeout(() => {
          if (cancelled) return;
          const expiring = new Set(spawned.map((sprite) => sprite.key));
          setSprites((current) => current.filter((sprite) => !expiring.has(sprite.key)));
        }, FLOAT_DURATION_MS);
      }
      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [input.enabled, input.events]);

  if (!input.enabled) return null;

  return (
    <div className="replayLayer" aria-hidden="true">
      {sprites.map((sprite) => {
        const icon = iconById.get(sprite.iconId);
        if (!icon) return null;
        return (
          <span
            key={sprite.key}
            className="replaySprite"
            style={{ left: `${sprite.leftPct}%`, top: `${sprite.topPct}%` }}
          >
            <TwemojiGlyphImage codepoint={icon.glyph_cp} label={icon.label} size={34} />
          </span>
        );
      })}
    </div>
  );
}
